import React from 'react';
import { motion } from 'framer-motion';
import { useNavigation } from '../NavigationContext';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .uei-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .uei-page { font-family: 'Inter', sans-serif; color: #0c1a36; background: #ffffff; width: 100%; }

  /* ── Breadcrumb ── */
  .uei-breadcrumb { max-width: 1200px; margin: 0 auto; padding: 24px 24px 0; display: flex; align-items: center; gap: 8px; font-size: 13px; color: #94a3b8; }
  .uei-breadcrumb-btn { background: none; border: none; cursor: pointer; color: #1e4d8c; font-size: 13px; font-weight: 600; padding: 0; display: inline-flex; align-items: center; gap: 5px; transition: color 0.2s; }
  .uei-breadcrumb-btn:hover { color: #c9931a; }

  /* ── Hero ── */
  .uei-hero { position: relative; padding: 60px 24px 100px; overflow: hidden; background: #fff; }
  .uei-hero-blob1 { position: absolute; top: -8%; right: -4%; width: 520px; height: 520px; border-radius: 50%; background: radial-gradient(circle, rgba(30,77,140,0.28), rgba(201,147,26,0.12)); filter: blur(80px); pointer-events: none; }
  .uei-hero-blob2 { position: absolute; bottom: -12%; left: -6%; width: 580px; height: 580px; border-radius: 50%; background: radial-gradient(circle, rgba(12,26,54,0.18), rgba(30,77,140,0.1)); filter: blur(80px); pointer-events: none; }
  .uei-hero-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: center; position: relative; z-index: 1; }
  @media (max-width: 900px) { .uei-hero-inner { grid-template-columns: 1fr; } .uei-hero-card { display: none; } }

  .uei-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 18px; border-radius: 999px; background: #fef3c7; border: 1px solid #fde68a; font-size: 12.5px; font-weight: 600; color: #92400e; margin-bottom: 28px; }
  .uei-hero h1 { font-size: clamp(1.725rem, 3.375vw, 2.85rem); font-weight: 900; line-height: 1.1; letter-spacing: -0.03em; color: #0c1a36; margin-bottom: 22px; }
  .uei-gradient { background: linear-gradient(135deg, #1e4d8c 0%, #c9931a 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .uei-hero-sub { font-size: 1.1rem; color: #475569; line-height: 1.82; margin-bottom: 36px; max-width: 520px; }
  .uei-btn-group { display: flex; gap: 14px; flex-wrap: wrap; }
  .uei-btn-primary { padding: 14px 28px; background: #0c1a36; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; }
  .uei-btn-primary:hover { background: #1e4d8c; transform: translateY(-1px); }
  .uei-btn-secondary { padding: 14px 28px; background: #fff; color: #334155; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .uei-btn-secondary:hover { border-color: #fde68a; background: #fffbeb; color: #92400e; }

  /* Hero Info Card */
  .uei-hero-card { background: #0c1a36; border-radius: 24px; padding: 36px; box-shadow: 0 32px 80px rgba(12,26,54,0.28); position: relative; overflow: hidden; }
  .uei-card-dots { position: absolute; inset: 0; opacity: 0.07; background-image: radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 0); background-size: 22px 22px; pointer-events: none; }
  .uei-card-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.45); margin-bottom: 18px; }
  .uei-card-title { font-size: 15px; font-weight: 700; color: #fff; margin-bottom: 20px; padding-bottom: 18px; border-bottom: 1px solid rgba(255,255,255,0.1); }
  .uei-fact { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 16px; }
  .uei-fact-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
  .uei-fact-icon.gold { background: rgba(201,147,26,0.18); }
  .uei-fact-icon.blue { background: rgba(30,77,140,0.3); }
  .uei-fact-icon.green { background: rgba(5,150,105,0.18); }
  .uei-fact-icon.red { background: rgba(220,38,38,0.18); }
  .uei-fact-title { font-size: 13px; font-weight: 700; color: #fff; margin-bottom: 3px; }
  .uei-fact-desc { font-size: 12px; color: rgba(255,255,255,0.55); line-height: 1.6; }
  .uei-card-highlight { margin-top: 20px; background: linear-gradient(135deg, rgba(201,147,26,0.2), rgba(201,147,26,0.08)); border: 1px solid rgba(201,147,26,0.3); border-radius: 14px; padding: 16px 18px; display: flex; align-items: center; gap: 12px; }
  .uei-card-highlight-num { font-size: 28px; font-weight: 900; color: #f59e0b; line-height: 1; }
  .uei-card-highlight-label { font-size: 12px; color: rgba(255,255,255,0.65); line-height: 1.5; }

  /* ── Shared ── */
  .uei-section { padding: 88px 24px; }
  .uei-container { max-width: 1200px; margin: 0 auto; }
  .uei-section-header { text-align: center; max-width: 720px; margin: 0 auto 60px; }
  .uei-section-header h2 { font-size: clamp(1.8rem, 3.2vw, 2.7rem); font-weight: 800; color: #0c1a36; letter-spacing: -0.02em; margin-bottom: 14px; line-height: 1.2; }
  .uei-section-header p { font-size: 1.03rem; color: #64748b; line-height: 1.8; }
  .uei-kicker { font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #1e4d8c; margin-bottom: 12px; }

  /* ── What Is UEI ── */
  .uei-what-bg { background: #f8fafc; border-top: 1px solid rgba(226,232,240,0.7); border-bottom: 1px solid rgba(226,232,240,0.7); }
  .uei-what-grid { display: grid; grid-template-columns: 1.1fr 1fr; gap: 80px; align-items: center; }
  @media (max-width: 900px) { .uei-what-grid { grid-template-columns: 1fr; gap: 48px; } }
  .uei-what-left h2 { font-size: clamp(1.8rem, 3vw, 2.5rem); font-weight: 800; color: #0c1a36; letter-spacing: -0.02em; line-height: 1.2; margin-bottom: 16px; }
  .uei-what-left p { font-size: 1rem; color: #475569; line-height: 1.82; margin-bottom: 28px; }
  .uei-check-list { display: flex; flex-direction: column; gap: 14px; }
  .uei-check-item { display: flex; align-items: flex-start; gap: 12px; }
  .uei-check-dot { width: 22px; height: 22px; border-radius: 50%; background: #fef3c7; border: 1.5px solid #fde68a; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #92400e; flex-shrink: 0; margin-top: 1px; font-weight: 800; }
  .uei-check-item p { font-size: 0.92rem; color: #334155; line-height: 1.68; }
  .uei-timeline-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 28px; box-shadow: 0 4px 20px rgba(0,0,0,0.04); }
  .uei-timeline-title { font-size: 13px; font-weight: 700; color: #0c1a36; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 1px solid #f1f5f9; }
  .uei-tl-row { display: flex; align-items: center; gap: 14px; padding: 12px 0; border-bottom: 1px solid #f8fafc; }
  .uei-tl-row:last-child { border-bottom: none; }
  .uei-tl-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
  .uei-tl-step { font-size: 12.5px; font-weight: 600; color: #334155; }
  .uei-tl-time { margin-left: auto; font-size: 11.5px; font-weight: 700; padding: 3px 10px; border-radius: 999px; }
  .uei-tl-time.fast { background: #ecfdf5; color: #059669; }
  .uei-tl-time.same { background: #eff6ff; color: #1e4d8c; }

  /* ── Who Needs One ── */
  .uei-who-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (max-width: 900px) { .uei-who-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 580px) { .uei-who-grid { grid-template-columns: 1fr; } }
  .uei-who-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 18px; padding: 28px 26px; transition: all 0.3s; }
  .uei-who-card:hover { background: #fff; box-shadow: 0 12px 40px rgba(0,0,0,0.07); transform: translateY(-2px); }
  .uei-who-icon { font-size: 28px; margin-bottom: 14px; }
  .uei-who-card h4 { font-size: 1rem; font-weight: 700; color: #0c1a36; margin-bottom: 8px; }
  .uei-who-card p { font-size: 0.88rem; color: #64748b; line-height: 1.68; }

  /* ── Common Errors (dark) ── */
  .uei-errors-bg { background: #0c1a36; position: relative; overflow: hidden; }
  .uei-errors-glow { position: absolute; top: -10%; right: 20%; width: 500px; height: 500px; background: radial-gradient(circle, rgba(201,147,26,0.14), transparent 70%); pointer-events: none; }
  .uei-errors-bg .uei-kicker { color: #fbbf24; }
  .uei-errors-bg .uei-section-header h2 { color: #fff; }
  .uei-errors-bg .uei-section-header p { color: #94a3b8; }
  .uei-errors-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
  @media (max-width: 700px) { .uei-errors-grid { grid-template-columns: 1fr; } }
  .uei-error-card { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); border-radius: 18px; padding: 28px; display: flex; gap: 16px; align-items: flex-start; transition: all 0.3s; }
  .uei-error-card:hover { border-color: rgba(201,147,26,0.35); background: rgba(255,255,255,0.07); }
  .uei-error-icon { flex-shrink: 0; width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 18px; background: rgba(220,38,38,0.15); }
  .uei-error-card h4 { font-size: 0.97rem; font-weight: 700; color: #fff; margin-bottom: 7px; }
  .uei-error-card p { font-size: 0.87rem; color: #94a3b8; line-height: 1.68; }

  /* ── Process ── */
  .uei-process-bg { background: #fff; }
  .uei-steps-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
  @media (max-width: 700px) { .uei-steps-row { grid-template-columns: 1fr; } }
  .uei-step-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 20px; padding: 36px 28px; text-align: center; transition: all 0.3s; }
  .uei-step-card:hover { background: #fff; box-shadow: 0 14px 44px rgba(0,0,0,0.08); transform: translateY(-2px); }
  .uei-step-num { width: 60px; height: 60px; border-radius: 50%; background: linear-gradient(135deg, #0c1a36, #1e4d8c); color: #fff; font-size: 1.3rem; font-weight: 900; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; box-shadow: 0 8px 24px rgba(12,26,54,0.3); }
  .uei-step-card h3 { font-size: 1.05rem; font-weight: 700; color: #0c1a36; margin-bottom: 10px; }
  .uei-step-card p { font-size: 0.88rem; color: #64748b; line-height: 1.72; }

  /* ── CTA ── */
  .uei-cta-bg { background: #fff; border-top: 1px solid #f1f5f9; }
  .uei-cta-inner { background: linear-gradient(135deg, #0c1a36 0%, #1a3568 60%, #0c1a36 100%); border-radius: 28px; padding: 80px 56px; text-align: center; position: relative; overflow: hidden; max-width: 900px; margin: 0 auto; }
  @media (max-width: 640px) { .uei-cta-inner { padding: 52px 28px; border-radius: 20px; } }
  .uei-cta-glow1 { position: absolute; top: -20%; right: -5%; width: 350px; height: 350px; background: radial-gradient(circle, rgba(201,147,26,0.22), transparent 65%); pointer-events: none; }
  .uei-cta-glow2 { position: absolute; bottom: -20%; left: -5%; width: 350px; height: 350px; background: radial-gradient(circle, rgba(30,77,140,0.3), transparent 65%); pointer-events: none; }
  .uei-cta-inner h2 { font-size: clamp(1.9rem, 3.5vw, 2.8rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1.15; margin-bottom: 16px; position: relative; z-index: 1; }
  .uei-cta-inner > p { font-size: 1.05rem; color: #94a3b8; max-width: 500px; margin: 0 auto 36px; line-height: 1.75; position: relative; z-index: 1; }
  .uei-cta-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
  .uei-cta-primary { padding: 14px 30px; background: #c9931a; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; box-shadow: 0 4px 18px rgba(201,147,26,0.4); }
  .uei-cta-primary:hover { background: #e0a820; transform: translateY(-1px); }
  .uei-cta-secondary { padding: 14px 30px; background: rgba(255,255,255,0.07); color: #cbd5e1; border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .uei-cta-secondary:hover { background: rgba(255,255,255,0.13); color: #fff; }
`;

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.55 },
};
const stagger = (i: number) => ({ ...fadeUp, transition: { duration: 0.5, delay: i * 0.09 } });

const whoCards = [
  { icon: '🏢', title: 'Businesses Seeking Federal Contracts', desc: 'Any company that wants to bid on or receive U.S. government contracts — regardless of size or industry — must have an active UEI.' },
  { icon: '🎓', title: 'Grant Recipients & Nonprofits', desc: 'Organizations applying for federal grants, loans, or assistance programs through grants.gov or any federal agency require a valid UEI.' },
  { icon: '🔁', title: 'Existing DUNS Holders', desc: 'Businesses that previously held a DUNS number were automatically transitioned to a UEI in April 2022. Verify yours is active and correctly linked.' },
  { icon: '🏗️', title: 'Subcontractors & Vendors', desc: 'Even businesses working as subcontractors under a prime federal contract may be required to have a UEI for compliance and payment reporting.' },
  { icon: '🌍', title: 'Foreign Entities', desc: 'Non-U.S. companies bidding on federal contracts or receiving federal funding must also obtain a UEI and complete SAM.gov registration.' },
  { icon: '🚀', title: 'Startups Entering GovCon', desc: 'New businesses entering the government contracting space need a UEI as their very first step — before anything else can proceed.' },
];

const errors = [
  { icon: '⚠️', title: 'Legal Name Mismatch', desc: 'The business name entered in SAM.gov does not exactly match the legal name on file with the IRS. Even a missing "LLC" or "Inc." will trigger a rejection.' },
  { icon: '⚠️', title: 'EIN / Tax ID Errors', desc: 'Entering the wrong Employer Identification Number (EIN) or using a Social Security Number instead of an EIN for a business entity causes immediate validation failure.' },
  { icon: '⚠️', title: 'Address Discrepancies', desc: 'The registered address in SAM.gov must match your official business address. P.O. boxes, suite numbers, and abbreviation differences all cause mismatches.' },
  { icon: '⚠️', title: 'Expired or Inactive Entity', desc: 'Applying for a UEI with an entity that is no longer in good standing with its state of incorporation will result in rejection during federal validation.' },
];

const processSteps = [
  { num: '01', title: 'Information Gathering', desc: 'We collect your legal business name, EIN, business address, and state of incorporation — verifying each against IRS and state records before submission.' },
  { num: '02', title: 'UEI Application', desc: 'We submit your UEI application through the official SAM.gov portal, ensuring every field matches your verified records to prevent rejection.' },
  { num: '03', title: 'Issuance & Verification', desc: 'Once your UEI is issued (typically within 24–48 hours), we verify the number, confirm it is active, and hand it off for use in your SAM.gov registration.' },
];

const UEIApplication: React.FC = () => {
  const { navigateTo } = useNavigation();

  return (
    <>
      <style>{styles}</style>
      <div className="uei-page">

        {/* Breadcrumb */}
        <div className="uei-breadcrumb">
          <button className="uei-breadcrumb-btn" onClick={() => navigateTo('')}>
            ← SAM.gov Registration
          </button>
          <span>/</span>
          <span>UEI Application</span>
        </div>

        {/* Hero */}
        <section className="uei-hero">
          <div className="uei-hero-blob1" />
          <div className="uei-hero-blob2" />
          <div className="uei-hero-inner">
            <motion.div {...fadeUp}>
              <div className="uei-badge">🪪 Step 1 of Federal Registration</div>
              <h1>
                Get Your <span className="uei-gradient">Unique Entity Identifier</span> — Your Entry Point to Federal Contracting
              </h1>
              <p className="uei-hero-sub">
                The UEI replaced the DUNS number in April 2022 as the required identifier for
                all federal contract and grant recipients. Without one, you cannot register on
                SAM.gov, bid on contracts, or receive federal payments.
              </p>
              <div className="uei-btn-group">
                <button className="uei-btn-primary">Apply for My UEI →</button>
                <button className="uei-btn-secondary">Talk to a Specialist</button>
              </div>
            </motion.div>

            <motion.div className="uei-hero-card" initial={{ opacity: 0, x: 36 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.65, delay: 0.2 }}>
              <div className="uei-card-dots" />
              <div className="uei-card-label">UEI Fast Facts</div>
              <div className="uei-card-title">Unique Entity Identifier — What You Need to Know</div>
              {[
                { icon: '🔄', color: 'blue', title: 'Replaced DUNS in April 2022', desc: 'SAM.gov now issues UEIs directly. No third-party registration required.' },
                { icon: '⏱️', color: 'green', title: '24–48 Hour Issuance', desc: 'Most UEIs are issued within 1–2 business days when submitted correctly.' },
                { icon: '🔢', color: 'gold', title: '12-Character Alphanumeric ID', desc: 'Your UEI is unique to your entity and used across all federal systems.' },
                { icon: '🚫', color: 'red', title: 'No UEI = No Federal Business', desc: 'Required for all federal contracts, grants, and assistance programs.' },
              ].map((f, i) => (
                <div key={i} className="uei-fact">
                  <div className={`uei-fact-icon ${f.color}`}>{f.icon}</div>
                  <div>
                    <div className="uei-fact-title">{f.title}</div>
                    <div className="uei-fact-desc">{f.desc}</div>
                  </div>
                </div>
              ))}
              <div className="uei-card-highlight">
                <div className="uei-card-highlight-num">Free</div>
                <div className="uei-card-highlight-label">UEI issuance through SAM.gov<br />is 100% free — beware of scams.</div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* What Is a UEI */}
        <section className="uei-section uei-what-bg">
          <div className="uei-container">
            <div className="uei-what-grid">
              <motion.div className="uei-what-left" {...fadeUp}>
                <div className="uei-kicker">What Is It</div>
                <h2>The Unique Entity Identifier Explained</h2>
                <p>
                  A UEI is a 12-character alphanumeric code assigned by SAM.gov to uniquely
                  identify your business across all federal systems. It replaced the previously
                  used DUNS number (issued by Dun & Bradstreet) when the federal government
                  moved to self-managed identifiers in April 2022.
                </p>
                <p>
                  Every federal agency — from the Department of Defense to the Small Business
                  Administration — uses your UEI to identify your entity in solicitations,
                  awards, payments, and compliance reporting.
                </p>
                <div className="uei-check-list">
                  {[
                    'Required for all federal contract awards and task orders',
                    'Required for federal grant applications via grants.gov',
                    'Required for federal loan and assistance programs',
                    'Tied to your CAGE code and SAM.gov registration profile',
                    'Must be active for your entity to receive federal payments',
                  ].map((item, i) => (
                    <div key={i} className="uei-check-item">
                      <div className="uei-check-dot">✓</div>
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div {...stagger(1)}>
                <div className="uei-timeline-card">
                  <div className="uei-timeline-title">UEI Application Timeline</div>
                  {[
                    { icon: '📋', color: '#eff6ff', step: 'Submit application with verified business info', time: 'Day 1', cls: 'same' },
                    { icon: '🔍', color: '#fef3c7', step: 'SAM.gov validates against IRS and entity records', time: '24 hrs', cls: 'same' },
                    { icon: '🪪', color: '#ecfdf5', step: 'UEI issued and available in SAM.gov', time: '24–48 hrs', cls: 'fast' },
                    { icon: '✅', color: '#ecfdf5', step: 'UEI verified and linked to SAM.gov registration', time: 'Same day', cls: 'fast' },
                  ].map((row, i) => (
                    <div key={i} className="uei-tl-row">
                      <div className="uei-tl-icon" style={{ background: row.color }}>{row.icon}</div>
                      <span className="uei-tl-step">{row.step}</span>
                      <span className={`uei-tl-time ${row.cls}`}>{row.time}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Who Needs One */}
        <section className="uei-section" style={{ background: '#fff' }}>
          <div className="uei-container">
            <motion.div className="uei-section-header" {...fadeUp}>
              <div className="uei-kicker">Who Needs a UEI</div>
              <h2>If You Want Federal Revenue, You Need a UEI</h2>
              <p>A Unique Entity Identifier is required for virtually every type of interaction with the federal government. Here is who needs one.</p>
            </motion.div>
            <div className="uei-who-grid">
              {whoCards.map((c, i) => (
                <motion.div key={i} className="uei-who-card" {...stagger(i)}>
                  <div className="uei-who-icon">{c.icon}</div>
                  <h4>{c.title}</h4>
                  <p>{c.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Common Errors */}
        <section className="uei-section uei-errors-bg">
          <div className="uei-errors-glow" />
          <div className="uei-container">
            <motion.div className="uei-section-header" {...fadeUp}>
              <div className="uei-kicker">Common Pitfalls</div>
              <h2>Why UEI Applications Get Rejected</h2>
              <p>These are the four most common reasons UEI applications fail — all of which our specialists prevent before submission.</p>
            </motion.div>
            <div className="uei-errors-grid">
              {errors.map((e, i) => (
                <motion.div key={i} className="uei-error-card" {...stagger(i)}>
                  <div className="uei-error-icon">{e.icon}</div>
                  <div>
                    <h4>{e.title}</h4>
                    <p>{e.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section className="uei-section uei-process-bg">
          <div className="uei-container">
            <motion.div className="uei-section-header" {...fadeUp}>
              <div className="uei-kicker">Our Process</div>
              <h2>How We Handle Your UEI Application</h2>
              <p>Three precise steps. Zero errors. Your UEI issued in 24–48 hours.</p>
            </motion.div>
            <div className="uei-steps-row">
              {processSteps.map((s, i) => (
                <motion.div key={i} className="uei-step-card" {...stagger(i)}>
                  <div className="uei-step-num">{s.num}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="uei-section uei-cta-bg">
          <div className="uei-container">
            <motion.div className="uei-cta-inner" {...fadeUp}>
              <div className="uei-cta-glow1" />
              <div className="uei-cta-glow2" />
              <h2>Ready to Get Your UEI?</h2>
              <p>Let our specialists handle your application — error-free, fast, and fully verified. Your federal contracting journey starts here.</p>
              <div className="uei-cta-btns">
                <button className="uei-cta-primary">Apply for My UEI →</button>
                <button className="uei-cta-secondary">Schedule a Free Consultation</button>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default UEIApplication;
